import { useState, useEffect, useCallback } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Stack } from '@mui/material';

import { ArrowCircleIcon } from 'app-zephyr-icons';
import { NavigationItem } from 'ze-api-contract/user-v2/navigation-items';

import { BreadcrumbType } from './interfaces';
import { useStyles } from './styles';
import { DropDownListItem } from './dropdown-list-item';

const MAX_DROPDOWN_ITEMS = 6;

export function BreadcrumbDropdownItem({
  itemList,
  type,
  selectedItem,
  onNavigationClick,
  onPinClick,
  onSelectClick,
  onViewAllClick,
}: {
  selectedItem?: string;
  type: BreadcrumbType;
  itemList: NavigationItem[];
  onNavigationClick: (name: string) => void;
  onPinClick: (name: string, isPinned: boolean) => void;
  onSelectClick: (name: string) => void;
  onViewAllClick: () => void;
}) {
  const [topItems, setTopItems] = useState<NavigationItem[]>([]);
  const [generalItems, setGeneralItems] = useState<NavigationItem[]>([]);
  const { classes } = useStyles();
  const [animationParent] = useAutoAnimate<HTMLDivElement>();

  useEffect(() => {
    const _selectedItem = itemList.find((item) => item.name === selectedItem);
    const _topItems = itemList.filter((item) => item.isPinned && item.name !== selectedItem);
    const _topItemsArr = _selectedItem ? [_selectedItem, ..._topItems] : _topItems;
    setTopItems(_topItemsArr);

    // -1 for selected item
    if (_topItems.length >= MAX_DROPDOWN_ITEMS - 1) return;
    const sliceNum =
      MAX_DROPDOWN_ITEMS - 1 - _topItems.length <= 0
        ? MAX_DROPDOWN_ITEMS - 1
        : MAX_DROPDOWN_ITEMS - 1 - _topItems.length;
    const _generalItems = itemList.filter((item) => !_topItemsArr.includes(item)).slice(0, sliceNum);
    setGeneralItems(_generalItems);
  }, [itemList, selectedItem]);

  const handleNavigationClick = useCallback(
    (name: string) => {
      onNavigationClick(name);
    },
    [onNavigationClick],
  );

  const handlePinClick = useCallback(
    (id: string, isPinned: boolean) => {
      onPinClick(id, isPinned);
    },
    [onPinClick],
  );

  const handleSelectClick = useCallback(
    (name: string) => {
      onSelectClick(name);
    },
    [onSelectClick],
  );

  return (
    <Stack justifyContent={'space-between'} height="100%">
      {!!itemList.length && (
        <Stack>
          {!!topItems.length && (
            <Stack gap={1} paddingBottom={2} borderBottom={'1px solid #1B1B1B'} ref={animationParent}>
              {topItems.map((item) => (
                <DropDownListItem
                  key={`dropdown-item-${item.name}`}
                  name={item.name}
                  selectedItem={selectedItem}
                  type={type}
                  isPinned={!!item.isPinned}
                  onNavigateClick={() => {
                    handleNavigationClick(item.name);
                  }}
                  onPinClick={() => {
                    handlePinClick(item.id, item.isPinned);
                  }}
                  onSelectClick={() => {
                    handleSelectClick(item.name);
                  }}
                />
              ))}
            </Stack>
          )}
          {!!generalItems.length && (
            <Stack gap={1} paddingTop={topItems.length ? 2 : 0} ref={animationParent}>
              {generalItems.map((item) => (
                <DropDownListItem
                  key={`dropdown-item-${item.name}`}
                  name={item.name}
                  selectedItem={selectedItem}
                  type={type}
                  isPinned={!!item.isPinned}
                  onNavigateClick={() => {
                    handleNavigationClick(item.name);
                  }}
                  onPinClick={() => {
                    handlePinClick(item.id, item.isPinned);
                  }}
                  onSelectClick={() => {
                    handleSelectClick(item.name);
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {itemList.length > MAX_DROPDOWN_ITEMS && (
        <Stack
          flexDirection={'row'}
          justifyContent={'center'}
          className={classes.allLink}
          marginTop={2}
          onClick={onViewAllClick}
        >
          View All <ArrowCircleIcon />
        </Stack>
      )}

      {!topItems.length && !generalItems.length && (
        <Stack flexDirection={'row'} justifyContent={'center'} className={classes.emptyItem}>
          No {type} selected so far!
        </Stack>
      )}
    </Stack>
  );
}
